import { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Truck,
  Building,
  Wrench,
  MapPin,
  Search,
  Eye,
  MessageSquare,
  CheckCircle2,
  Plus,
  Filter,
  X,
  Clock,
  IndianRupee,
} from "lucide-react";
import api from "@/lib/api";
import { AuthContext } from "@/App";
import { toast } from "sonner";

const CATEGORIES = {
  equipment: {
    name: "Equipment",
    icon: Wrench,
    color: "bg-orange-500",
    lightColor: "bg-orange-50",
    textColor: "text-orange-600",
    subcategories: ["Hywa", "JCB", "Crane", "Forklift", "Excavator", "Bulldozer", "Concrete Mixer", "Generator", "Other Equipment"]
  },
  spaces: {
    name: "Spaces",
    icon: Building,
    color: "bg-blue-500",
    lightColor: "bg-blue-50",
    textColor: "text-blue-600",
    subcategories: ["Commercial Shop", "Warehouse", "Godown", "Co-working Space", "Office Space", "Industrial Shed", "Cold Storage", "Other Space"]
  },
  transport: {
    name: "Transport",
    icon: Truck,
    color: "bg-green-500",
    lightColor: "bg-green-50",
    textColor: "text-green-600",
    subcategories: ["Truck", "Tempo", "Container", "Tanker", "Trailer", "Mini Truck", "Fleet Services", "Logistics", "Other Transport"]
  }
};

export default function Rentals() {
  const { user } = useContext(AuthContext);
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedSubcategory, setSelectedSubcategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchRentals();
  }, [selectedCategory, selectedSubcategory, locationFilter]);

  const fetchRentals = async () => {
    try {
      setLoading(true);
      let url = "/rentals?status=active";
      if (selectedCategory !== "all") {
        url += `&category=${selectedCategory}`;
      }
      if (selectedSubcategory !== "all") {
        url += `&subcategory=${selectedSubcategory}`;
      }
      if (locationFilter) {
        url += `&location=${encodeURIComponent(locationFilter)}`;
      }
      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }
      const { data } = await api.get(url);
      setRentals(data);
    } catch (error) {
      console.error("Failed to fetch rentals:", error);
      toast.error("Failed to load rentals");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchRentals();
  };

  const clearFilters = () => {
    setSelectedCategory("all");
    setSelectedSubcategory("all");
    setSearchQuery("");
    setLocationFilter("");
  };

  const formatPrice = (rental) => {
    if (rental.is_negotiable && !rental.hourly_rate && !rental.daily_rate && !rental.monthly_rate) {
      return "Contact for Price";
    }
    
    const prices = [];
    if (rental.hourly_rate) prices.push(`₹${rental.hourly_rate.toLocaleString('en-IN')}/hr`);
    if (rental.daily_rate) prices.push(`₹${rental.daily_rate.toLocaleString('en-IN')}/day`);
    if (rental.monthly_rate) prices.push(`₹${rental.monthly_rate.toLocaleString('en-IN')}/mo`);
    
    return prices.length > 0 ? prices[0] : "Contact for Price";
  };

  const getCategoryIcon = (category) => {
    const cat = CATEGORIES[category];
    if (!cat) return Wrench;
    return cat.icon;
  };

  const getCategoryStyles = (category) => {
    return CATEGORIES[category] || CATEGORIES.equipment;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-16" data-testid="rentals-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              B2B Rentals Marketplace
            </h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Rent equipment, commercial spaces, and transport services for your business needs
            </p>
          </div>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="max-w-3xl mx-auto">
            <div className="flex gap-2 bg-white p-2 rounded-xl shadow-lg">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder="Search rentals..."
                  className="pl-10 border-0 focus-visible:ring-0 h-12"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="rentals-search-input"
                />
              </div>
              <Button type="submit" className="h-12 px-6 bg-blue-600 hover:bg-blue-700" data-testid="rentals-search-btn">
                Search
              </Button>
            </div>
          </form>
        </div>
      </section>

      {/* Category Pills */}
      <section className="py-8 bg-white border-b" data-testid="rentals-categories">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Button
              variant={selectedCategory === "all" ? "default" : "outline"}
              onClick={() => { setSelectedCategory("all"); setSelectedSubcategory("all"); }}
              className={selectedCategory === "all" ? "bg-slate-900" : ""}
              data-testid="category-all"
            >
              All Categories
            </Button>
            {Object.entries(CATEGORIES).map(([key, cat]) => {
              const Icon = cat.icon;
              return (
                <Button
                  key={key}
                  variant={selectedCategory === key ? "default" : "outline"}
                  onClick={() => { setSelectedCategory(key); setSelectedSubcategory("all"); }}
                  className={selectedCategory === key ? cat.color : ""}
                  data-testid={`category-${key}`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {cat.name}
                </Button>
              );
            })}
            
            {user && (
              <Link to="/list-rental">
                <Button className="bg-emerald-600 hover:bg-emerald-700 ml-4" data-testid="list-rental-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  List Your Rental
                </Button>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Filters & Results */}
      <section className="py-8" data-testid="rentals-results">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filter Row */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowFilters(!showFilters)}
                data-testid="toggle-filters-btn"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
              </Button>
              
              {selectedCategory !== "all" && (
                <Select value={selectedSubcategory} onValueChange={setSelectedSubcategory}>
                  <SelectTrigger className="w-[180px]" data-testid="subcategory-select">
                    <SelectValue placeholder="Subcategory" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Subcategories</SelectItem>
                    {CATEGORIES[selectedCategory]?.subcategories.map((sub) => (
                      <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              {(selectedCategory !== "all" || searchQuery || locationFilter) && (
                <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>
            
            <p className="text-sm text-slate-600">
              {rentals.length} rental{rentals.length !== 1 ? "s" : ""} found
            </p>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="bg-white p-4 rounded-lg border mb-6 flex flex-wrap gap-4" data-testid="expanded-filters">
              <div className="flex-1 min-w-[200px]">
                <label className="text-sm font-medium text-slate-700 mb-1 block">Location</label>
                <Input
                  placeholder="Enter city or area"
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  data-testid="location-filter-input"
                />
              </div>
              <div className="flex items-end">
                <Button onClick={fetchRentals} data-testid="apply-filters-btn">Apply Filters</Button>
              </div>
            </div>
          )}

          {/* Results Grid */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-slate-600">Loading rentals...</p>
            </div>
          ) : rentals.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border">
              <Wrench className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No Rentals Found</h3>
              <p className="text-slate-600 mb-6">Try adjusting your filters or search terms</p>
              {user && (
                <Link to="/list-rental">
                  <Button className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-2" />
                    List Your First Rental
                  </Button>
                </Link>
              )}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rentals.map((rental) => {
                const catStyles = getCategoryStyles(rental.category);
                const Icon = getCategoryIcon(rental.category);
                return (
                  <Card key={rental.id} className="group overflow-hidden hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-blue-200" data-testid={`rental-card-${rental.id}`}>
                    <div className="relative">
                      {rental.images && rental.images.length > 0 ? (
                        <img
                          src={rental.images[0]}
                          alt={rental.title}
                          className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className={`w-full h-48 ${catStyles.lightColor} flex items-center justify-center`}>
                          <Icon className={`w-16 h-16 ${catStyles.textColor} opacity-50`} />
                        </div>
                      )}
                      <div className="absolute top-3 left-3 flex gap-2">
                        <Badge className={`${catStyles.color} text-white`}>
                          {catStyles.name}
                        </Badge>
                        {rental.verified && (
                          <Badge className="bg-emerald-500 text-white">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Verified
                          </Badge>
                        )}
                      </div>
                      <div className="absolute top-3 right-3">
                        <Badge variant="secondary" className="bg-white/90">
                          {rental.subcategory}
                        </Badge>
                      </div>
                    </div>
                    <CardContent className="p-5">
                      <h3 className="text-lg font-bold text-slate-900 mb-2 line-clamp-1">
                        {rental.title}
                      </h3>
                      <p className="text-sm text-slate-600 mb-3 line-clamp-2">
                        {rental.description}
                      </p>
                      
                      <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {rental.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="w-4 h-4" />
                          {rental.views}
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageSquare className="w-4 h-4" />
                          {rental.enquiries_count}
                        </span>
                      </div>

                      {/* Pricing */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {rental.is_negotiable && !rental.hourly_rate && !rental.daily_rate && !rental.monthly_rate ? (
                          <Badge variant="outline" className="text-blue-600 border-blue-200">
                            Contact for Price
                          </Badge>
                        ) : (
                          <>
                            {rental.hourly_rate && (
                              <Badge variant="outline" className="text-slate-700">
                                <IndianRupee className="w-3 h-3 mr-1" />
                                {rental.hourly_rate.toLocaleString('en-IN')}/hr
                              </Badge>
                            )}
                            {rental.daily_rate && (
                              <Badge variant="outline" className="text-slate-700">
                                <IndianRupee className="w-3 h-3 mr-1" />
                                {rental.daily_rate.toLocaleString('en-IN')}/day
                              </Badge>
                            )}
                            {rental.monthly_rate && (
                              <Badge variant="outline" className="text-slate-700">
                                <IndianRupee className="w-3 h-3 mr-1" />
                                {rental.monthly_rate.toLocaleString('en-IN')}/mo
                              </Badge>
                            )}
                            {rental.is_negotiable && (
                              <Badge variant="outline" className="text-green-600 border-green-200">
                                Negotiable
                              </Badge>
                            )}
                          </>
                        )}
                      </div>

                      {/* Availability */}
                      <div className="flex items-center justify-between pt-4 border-t">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-slate-400" />
                          <span className={`text-sm font-medium ${
                            rental.availability === "Available" ? "text-emerald-600" :
                            rental.availability === "Limited" ? "text-amber-600" : "text-slate-600"
                          }`}>
                            {rental.availability}
                          </span>
                        </div>
                        <Link to={`/rentals/${rental.id}`}>
                          <Button size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid={`view-rental-${rental.id}`}>
                            View Details
                          </Button>
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
